import React from 'react';

/**
 * Converts a hex color string to its relative luminance (0–1) per WCAG 2.1.
 * Works with 3- or 6-digit hex codes, with or without a leading '#'.
 *
 * @param hex - A CSS hex color string, e.g. "#121212" or "FBF9F4".
 * @returns Relative luminance in the range [0, 1], or NaN if unparseable.
 */
const hexToLuminance = (hex: string): number => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean;
  const [r, g, b] = [0, 2, 4].map(i => {
    const val = parseInt(full.slice(i, i + 2), 16) / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Linearises a single sRGB channel value (0–255) to relative luminance.
 *
 * @param channel - Integer channel value 0–255.
 * @returns Linearised luminance contribution.
 */
const linearise = (channel: number): number => {
  const val = channel / 255;
  return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
};

/**
 * Parses `rgb(r, g, b)` or `rgba(r, g, b, a)` strings and returns relative luminance.
 *
 * @param color - A CSS rgb/rgba color string.
 * @returns Relative luminance in [0, 1], or NaN if the string does not match.
 */
const rgbToLuminance = (color: string): number => {
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return NaN;
  return (
    0.2126 * linearise(parseInt(match[1], 10)) +
    0.7152 * linearise(parseInt(match[2], 10)) +
    0.0722 * linearise(parseInt(match[3], 10))
  );
};

/**
 * Returns a high-contrast foreground color (white or near-black) for any
 * background, based on WCAG relative luminance.
 *
 * Supports hex (#rgb / #rrggbb), rgb(...), and rgba(...) color formats.
 * Falls back to `lightColor` for unparseable values (e.g. CSS variables,
 * 'transparent') — white text is readable on the majority of unknown dark
 * backgrounds encountered in practice.
 *
 * @param background - CSS color value of the background.
 * @param lightColor - Color to use on dark backgrounds. Default: '#FFFFFF'.
 * @param darkColor  - Color to use on light backgrounds. Default: '#1A1A1A'.
 * @returns The appropriate foreground color string.
 */
export const getContrastColor = (
  background: string,
  lightColor = '#FFFFFF',
  darkColor = '#1A1A1A'
): string => {
  if (!background) return lightColor;

  const trimmed = background.trim();

  // Try hex first, then rgb/rgba.
  let lum = trimmed.startsWith('#') ? hexToLuminance(trimmed) : NaN;
  if (isNaN(lum)) lum = rgbToLuminance(trimmed);

  // When the format is unrecognised (CSS variable, named colour, 'transparent'),
  // assume the background is dark and return light text. This matches the
  // implicit previous behaviour where NaN > 0.179 → false → lightColor.
  if (isNaN(lum)) return lightColor;

  return lum > 0.179 ? darkColor : lightColor;
};

/**
 * Truncates a string to a maximum length, breaking on the nearest word
 * boundary to avoid cutting mid-word, and appending an ellipsis.
 *
 * @param text - The raw description string from the search result.
 * @param maxLength - Maximum number of characters before truncation. Default: 200.
 * @returns The truncated string with a trailing ellipsis, or the original string.
 */
export const truncateDescription = (text: string, maxLength = 200): string => {
  if (!text || text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '\u2026';
};

/**
 * Splits `text` on every occurrence of `keyword` (case-insensitive) and
 * wraps each match in a `<mark>` element for visual keyword highlighting.
 *
 * @param text - The string in which to highlight matches.
 * @param keyword - The search term to highlight.
 * @returns A React node with matched substrings wrapped in `<mark>`.
 */
export const highlightKeyword = (
  text: string,
  keyword: string
): React.ReactNode => {
  if (!keyword || !text) return text;

  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark
            key={i}
            style={{
              backgroundColor: '#ffd700',
              color: '#000',
              borderRadius: '2px',
              padding: '0 1px',
            }}
          >
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};
